// ============================================
// Clinical Awareness Service — Shared
// ============================================
// Provides autonomous data retrieval for clinical agents (Nurse, Eddie).
// Resolves extracted labs from Firestore based on patientId.
// ============================================

import { firestore } from "../firestoreAdmin";
import { C_LAB_EXTRACTIONS, C_PATIENT_JOURNEYS } from "./collections";
import { getBiomarkerById } from "./biomarkerCatalog";

export interface BiomarkerResult {
  biomarker: string;
  value: number | string;
  unit: string;
  hemologixStatus: 'optimal' | 'suboptimal_low' | 'suboptimal_high' | 'low' | 'high';
  category: string;
  standardRange: { low: number; high: number };
  functionalRange: { low: number; high: number };
}

export interface ClinicalContext {
  biomarkers: BiomarkerResult[];
  quarantined: any[];
  concerns: string[];
}

/**
 * Platinum: Automatic clinical awareness.
 * Queries Firestore for the latest extraction result and journey data for this patient.
 */
export async function getLatestPatientLabs(patientId: string): Promise<ClinicalContext> {
  try {
    // 1. Fetch latest extraction
    const extractionSnap = await firestore.collection(C_LAB_EXTRACTIONS)
      .where('patientId', '==', patientId)
      .orderBy('extractedAt', 'desc')
      .limit(1)
      .get();

    // 2. Fetch latest journey (for concerns/context)
    const journeySnap = await firestore.collection(C_PATIENT_JOURNEYS)
      .where('patientId', '==', patientId)
      .orderBy('createdAt', 'desc')
      .limit(1)
      .get();

    const concerns = journeySnap.empty ? [] : (journeySnap.docs[0].data().triageData?.concerns || []);

    if (extractionSnap.empty) return { biomarkers: [], quarantined: [], concerns };
    
    const data = extractionSnap.docs[0].data();
    
    // Platinum: Deduplication & Highest Confidence selection
    // extractLabPDF stores approved biomarkers in 'biomarkerSummary' field
    const rawBiomarkers = data.biomarkerSummary || data.biomarkers || [];
    const biomarkersMap = new Map<string, any>();
    rawBiomarkers.forEach((lab: any) => {
      const key = lab.canonicalId || lab.rawName;
      if (!biomarkersMap.has(key) || (lab.confidence || 0) > (biomarkersMap.get(key).confidence || 0)) {
        biomarkersMap.set(key, lab);
      }
    });

    const uniqueLabs = Array.from(biomarkersMap.values());

    const biomarkers = uniqueLabs.map((lab: any) => {
      const catalog = getBiomarkerById(lab.canonicalId);

      // Use catalog ranges if available; fall back to lab-reported standardRange
      const stdRange = catalog?.conventionalRange ?? {
        low: lab.standardRange?.low ?? lab.standardRange?.min ?? null,
        high: lab.standardRange?.high ?? lab.standardRange?.max ?? null,
      };
      const funcRange = catalog?.functionalRange ?? null;

      let status: any = 'optimal';
      const val = typeof lab.value === 'number' ? lab.value : parseFloat(lab.value);
      if (!isNaN(val)) {
        if (stdRange.low !== null && val < stdRange.low) status = 'low';
        else if (stdRange.high !== null && val > stdRange.high) status = 'high';
        else if (funcRange && funcRange.low !== null && val < funcRange.low) status = 'suboptimal_low';
        else if (funcRange && funcRange.high !== null && val > funcRange.high) status = 'suboptimal_high';
      }

      return {
        biomarker: catalog?.name ?? lab.rawName ?? lab.canonicalId,
        value: val,
        unit: lab.unit,
        hemologixStatus: status,
        category: catalog?.categories?.[0] ?? 'general',
        standardRange: { low: stdRange.low ?? 0, high: stdRange.high ?? 0 },
        functionalRange: funcRange ? { low: funcRange.low ?? 0, high: funcRange.high ?? 0 } : { low: 0, high: 0 },
      } as BiomarkerResult;
    }).filter((b): b is BiomarkerResult => b !== null);

    return { 
      biomarkers, 
      quarantined: data.quarantinedBiomarkers || [],
      concerns 
    };
  } catch (err) {
    console.error('[ClinicalAwareness] Failed to load labs:', err);
    return { biomarkers: [], quarantined: [], concerns: [] };
  }
}
